import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';

/**
 * EstimateTable: Dynamic rows component for CTP/RIP estimate line items
 * - Auto-adds new empty rows when last row has data
 * - Auto-calculates totals: totalNTECost = (quantity × unitCost) + authorizedIndirectCosts
 * - Cleans up empty rows on blur
 * - Shows validation errors when field is required and touched (via dirtyMap)
 *
 * Data structure matches API format:
 * estimates: [{
 *   estimateType: 'CTPEstimate' | 'RIPEstimate',
 *   totalEstimate: number,
 *   lineItems: [{
 *     lineItem: number,
 *     description: string,
 *     quantity: number,
 *     unitType: string,
 *     unitCost: number,
 *     authorizedIndirectCosts: number,
 *     totalNTECost: number,
 *     totalLineItemEstimate: number
 *   }]
 * }]
 */
export default function EstimateTable(props) {
  const fieldName = props.field.name || 'estimates';
  const isRequired = Boolean(props.field.required || props.field.requiredToSubmit);
  const isDirty = Boolean(props.dirtyMap?.[fieldName]);
  const estimateType = props.field.estimateType || 'CTPEstimate';
  const estimates = useMemo(() => props.formData?.[fieldName] || [], [props.formData, fieldName]);

  // Get the first estimate (CTP/RIP typically have one estimate)
  const estimate = useMemo(() => estimates[0] || { estimateType, totalEstimate: 0, lineItems: [] }, [estimates, estimateType]);
  const lineItems = useMemo(() => estimate.lineItems || [], [estimate]);

  // Calculate total estimate from line items
  const calculatedTotal = useMemo(() => {
    return lineItems.reduce((sum, item) => sum + (item.totalLineItemEstimate || 0), 0);
  }, [lineItems]);

  // Determine if a row has any data entered
  const rowHasData = (item) =>
    (item.description && String(item.description).trim().length > 0) ||
    (item.quantity && Number(item.quantity) > 0) ||
    (item.unitCost && Number(item.unitCost) > 0) ||
    (item.authorizedIndirectCosts && Number(item.authorizedIndirectCosts) > 0);

  // Check if the table has at least one filled row
  const hasAnyFilledRow = useMemo(() => lineItems.some(rowHasData), [lineItems]);

  // Table-level error: required + dirty + no filled rows
  const tableError = isRequired && isDirty && !hasAnyFilledRow;

  // Per-cell error: only check on non-empty rows when dirty
  const cellError = (item, cellField) => {
    if (!isRequired || !isDirty) return false;
    if (!rowHasData(item)) return false;
    if (cellField === 'description') return !item.description || String(item.description).trim().length === 0;
    if (cellField === 'quantity') return !item.quantity || Number(item.quantity) <= 0;
    if (cellField === 'unitCost') return !item.unitCost || Number(item.unitCost) <= 0;
    if (cellField === 'authorizedIndirectCosts') return item.authorizedIndirectCosts == null || String(item.authorizedIndirectCosts).trim() === '';
    return false;
  };

  // Update parent formData with new estimates array
  const updateEstimates = useCallback((newLineItems) => {
    const newEstimate = {
      ...estimate,
      estimateType,
      lineItems: newLineItems,
      totalEstimate: newLineItems.reduce((sum, item) => sum + (item.totalLineItemEstimate || 0), 0),
    };

    props.setFormData?.((prev) => ({
      ...prev,
      [fieldName]: [newEstimate],
    }));
  }, [estimate, estimateType, fieldName, props.setFormData]);

  // Mark dirty on first interaction
  const markDirty = () => {
    if (!isDirty) {
      props.setDirtyMap?.((prev) => ({ ...prev, [fieldName]: true }));
    }
  };

  // Initialize with at least one empty row if no data exists
  const emptyRow = (lineItem) => ({
    lineItem,
    description: '',
    quantity: '',
    unitType: 'Unit',
    unitCost: '',
    authorizedIndirectCosts: '',
    totalNTECost: 0,
    totalLineItemEstimate: 0,
  });

  useEffect(() => {
    if (lineItems.length === 0 && !props.readOnly && estimates.length === 0) {
      updateEstimates([emptyRow(1)]);
    }
  }, [lineItems.length, props.readOnly, estimates.length, updateEstimates]);

  // Auto-add new empty row when last row has data
  useEffect(() => {
    if (lineItems.length === 0 || props.readOnly) return;

    const lastItem = lineItems[lineItems.length - 1];
    const hasData = lastItem.description || lastItem.quantity || lastItem.unitCost || lastItem.authorizedIndirectCosts;

    if (hasData) {
      updateEstimates([...lineItems, emptyRow(lineItems.length + 1)]);
    }
  }, [lineItems, props.readOnly, updateEstimates]);

  const updateCell = (index, field, value) => {
    if (props.readOnly) return;
    markDirty();

    const newItems = [...lineItems];
    const item = { ...newItems[index] };

    // Parse numeric values
    if (['quantity', 'unitCost', 'authorizedIndirectCosts'].includes(field)) {
      item[field] = parseFloat(value) || 0;
    } else {
      item[field] = value;
    }

    // Calculate totals
    if (field === 'quantity' || field === 'unitCost' || field === 'authorizedIndirectCosts') {
      const qty = item.quantity || 0;
      const unitCost = item.unitCost || 0;
      const indirect = item.authorizedIndirectCosts || 0;

      // totalNTECost = (quantity * unitCost) + indirect
      item.totalNTECost = (qty * unitCost) + indirect;
      item.totalLineItemEstimate = item.totalNTECost;
    }

    newItems[index] = item;
    updateEstimates(newItems);
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Clean up empty rows on blur, keeping exactly one empty row at the end
  const handleBlur = () => {
    if (props.readOnly) return;

    const isEmptyRow = (item) => !item?.description && !item?.quantity && !item?.unitCost && !item?.authorizedIndirectCosts;

    // Keep all non-empty rows
    const nonEmptyRows = lineItems.filter(item => !isEmptyRow(item));

    // Add exactly one empty row at the end
    const updated = [
      ...nonEmptyRows,
      emptyRow(nonEmptyRows.length + 1),
    ];

    // Renumber lineItem fields
    const renumbered = updated.map((item, i) => ({ ...item, lineItem: i + 1 }));

    if (renumbered.length !== lineItems.length) {
      updateEstimates(renumbered);
    }
  };

  return (
    <Box
      sx={{
        mb: 1.5,
        ...(tableError ? {
          border: '1px solid',
          borderColor: 'error.main',
          borderRadius: 1,
          p: 1.5,
        } : {}),
      }}
    >
      {props.field.label && (
        <Typography
          variant="subtitle1"
          gutterBottom
          color={tableError ? 'error' : 'textPrimary'}
        >
          {props.field.label}{isRequired ? ' *' : ''}
          {tableError && (
            <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
              At least one estimate line item is required
            </Typography>
          )}
        </Typography>
      )}

      {lineItems.map((item, idx) => {
        const hasData = rowHasData(item);
        return (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            mb: 1,
            position: 'relative',
          }}
          onBlur={handleBlur}
        >
          {/* Item Description */}
          <Box flex="3 1 350px">
            <TextField
              id={`field-${fieldName}-${idx}-description`}
              variant="outlined"
              label="Item Description"
              value={item.description || ''}
              onChange={(e) => updateCell(idx, 'description', e.target.value)}
              fullWidth
              required={hasData}
              error={cellError(item, 'description')}
              InputProps={{ readOnly: props.readOnly }}
              slotProps={{
                htmlInput: {
                  id: `field-${fieldName}-${idx}-description`,
                  name: `${fieldName}.${idx}.description`,
                }
              }}
            />
          </Box>

          {/* Quantity */}
          <Box flex="1 1 120px">
            <TextField
              id={`field-${fieldName}-${idx}-quantity`}
              variant="outlined"
              label="Quantity"
              value={item.quantity || ''}
              onChange={(e) => updateCell(idx, 'quantity', e.target.value)}
              fullWidth
              type="number"
              required={hasData}
              error={cellError(item, 'quantity')}
              InputProps={{ readOnly: props.readOnly }}
              slotProps={{
                htmlInput: {
                  id: `field-${fieldName}-${idx}-quantity`,
                  name: `${fieldName}.${idx}.quantity`,
                  min: 0,
                }
              }}
            />
          </Box>

          {/* Item/Unit Cost with $ adornment */}
          <Box flex="1 1 150px">
            <TextField
              id={`field-${fieldName}-${idx}-unitCost`}
              variant="outlined"
              label="Item/Unit Cost"
              value={item.unitCost || ''}
              onChange={(e) => updateCell(idx, 'unitCost', e.target.value)}
              fullWidth
              type="number"
              required={hasData}
              error={cellError(item, 'unitCost')}
              InputProps={{
                readOnly: props.readOnly,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              slotProps={{
                htmlInput: {
                  id: `field-${fieldName}-${idx}-unitCost`,
                  name: `${fieldName}.${idx}.unitCost`,
                  min: 0,
                  step: 0.01,
                }
              }}
            />
          </Box>

          {/* Indirect Costs Authorized with $ adornment */}
          <Box flex="1 1 180px">
            <TextField
              id={`field-${fieldName}-${idx}-authorizedIndirectCosts`}
              variant="outlined"
              label={`Indirect Costs (${props.field.indirectCostLabel || 'Authorized'})`}
              value={item.authorizedIndirectCosts || ''}
              onChange={(e) => updateCell(idx, 'authorizedIndirectCosts', e.target.value)}
              fullWidth
              type="number"
              required={hasData}
              error={cellError(item, 'authorizedIndirectCosts')}
              InputProps={{
                readOnly: props.readOnly,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              slotProps={{
                htmlInput: {
                  id: `field-${fieldName}-${idx}-authorizedIndirectCosts`,
                  name: `${fieldName}.${idx}.authorizedIndirectCosts`,
                  min: 0,
                  step: 0.01,
                }
              }}
            />
          </Box>

          {/* Total NTE Cost (read-only calculated field) with $ adornment */}
          <Box flex="1 1 180px">
            <TextField
              id={`field-${fieldName}-${idx}-totalLineItemEstimate`}
              variant="outlined"
              label="Total NTE Cost"
              value={formatCurrency(item.totalLineItemEstimate)}
              fullWidth
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              slotProps={{
                htmlInput: {
                  id: `field-${fieldName}-${idx}-totalLineItemEstimate`,
                  name: `${fieldName}.${idx}.totalLineItemEstimate`,
                }
              }}
            />
          </Box>
        </Box>
        );
      })}

      {/* Total Estimated Cost Summary */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mt: 2,
          gap: 2,
        }}
      >
        <Typography variant="body1" sx={{ fontSize: '1.25rem' }}>
          TOTAL ESTIMATED COST:
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.25rem', color: tableError ? 'error.main' : 'primary.main', minWidth: 120, textAlign: 'right' }}>
          ${formatCurrency(calculatedTotal)}
        </Typography>
      </Box>
    </Box>
  );
}
